import { type WmtsDebugTile, wmtsTilesToDebugFeatureCollection } from "@/layers/png/WMTSLayerImpl/wmtsDebugGrid";
import type * as mapboxgl from "mapbox-gl";

type RequestedTileGridOverlayOptions = {
  color?: string;
  width?: number;
  opacity?: number;
};

const DEFAULT_OPTIONS: Required<RequestedTileGridOverlayOptions> = {
  color: "#ff2b2b",
  width: 3,
  opacity: 0.9,
};

export class RequestedTileGridOverlay {
  private lastTileKey: string | null = null;
  private readonly style: Required<RequestedTileGridOverlayOptions>;

  constructor(
    private readonly sourceId: string,
    private readonly layerId: string,
    options?: RequestedTileGridOverlayOptions,
  ) {
    this.style = { ...DEFAULT_OPTIONS, ...options };
  }

  sync(map: mapboxgl.Map, tiles: WmtsDebugTile[], enabled: boolean): void {
    if (!enabled) {
      this.remove(map);
      return;
    }

    try {
      this.ensureLayer(map);
    } catch {
      return;
    }

    const tileKey = tiles
      .map((tile) => `${tile.z}/${tile.x}/${tile.y}`)
      .sort()
      .join("|");
    if (tileKey === this.lastTileKey) {
      return;
    }

    this.lastTileKey = tileKey;
    const source = map.getSource(this.sourceId) as mapboxgl.GeoJSONSource | undefined;
    if (!source) {
      return;
    }

    try {
      source.setData(wmtsTilesToDebugFeatureCollection(tiles));
    } catch {}
  }

  remove(map: mapboxgl.Map): void {
    this.lastTileKey = null;
    try {
      if (map.getLayer(this.layerId)) {
        map.removeLayer(this.layerId);
      }
      if (map.getSource(this.sourceId)) {
        map.removeSource(this.sourceId);
      }
    } catch {}
  }

  private ensureLayer(map: mapboxgl.Map): void {
    if (!map.getSource(this.sourceId)) {
      map.addSource(this.sourceId, {
        type: "geojson",
        data: wmtsTilesToDebugFeatureCollection([]),
      });
    }

    if (!map.getLayer(this.layerId)) {
      map.addLayer({
        id: this.layerId,
        type: "line",
        source: this.sourceId,
        paint: {
          "line-color": this.style.color,
          "line-width": this.style.width,
          "line-opacity": this.style.opacity,
        },
        layout: {
          "line-join": "round",
          "line-cap": "round",
        },
      });
    }
    map.moveLayer(this.layerId);
  }
}
